import React, { useState, useEffect, useRef } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
// @ts-ignore
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { parseLocalDate } from '../../utils/dateUtils';
import { getHolidayRange } from '../../utils/holidayUtils';
import './DateRangeFilter.css';

interface DateRangeFilterProps {
  onDateRangeChange: (startDate: Date | null, endDate: Date | null) => void;
  initialStartDate?: string;
  initialEndDate?: string;
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  onDateRangeChange,
  initialStartDate,
  initialEndDate
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const createLocalDate = (dateString?: string) => {
    if (!dateString) return new Date();
    return parseLocalDate(dateString);
  };

  const [dateRange, setDateRange] = useState([
    {
      startDate: createLocalDate(initialStartDate),
      endDate: createLocalDate(initialEndDate),
      key: 'selection'
    }
  ]);
  const [datesCleared, setDatesCleared] = useState(!initialStartDate && !initialEndDate);

  const [isWideScreen, setIsWideScreen] = useState(true);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsWideScreen(window.innerWidth > 1200);
    };

    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        // Check if the click was on the button itself
        const container = pickerRef.current?.parentElement;
        const button = container?.querySelector('.filter-button-date');
        if (button && button.contains(event.target as Node)) {
          return; // Don't close if clicking the button
        }
        setShowDatePicker(false);
      }
    }

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setDateRange([{
      startDate: createLocalDate(initialStartDate),
      endDate: createLocalDate(initialEndDate),
      key: 'selection'
    }]);
    setDatesCleared(!initialStartDate && !initialEndDate);
  }, [initialStartDate, initialEndDate]);

  const handleDateChange = (ranges: any) => {
    setDatesCleared(false);
    setDateRange([ranges.selection]);
    onDateRangeChange(ranges.selection.startDate, ranges.selection.endDate);
  };

  const formatDateRange = () => {
    const start = dateRange[0].startDate;
    const end = dateRange[0].endDate;
    
    if (datesCleared) {
      return "Any Date";
    }

    return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
  };

  const getHolidayDates = (preset: 'next-month' | 'thanksgiving' | 'winter-break' | 'spring-break' | 'summer-break') => {
    return getHolidayRange(preset);
  };

  const handlePresetClick = (preset: 'today' | 'next-month' | 'thanksgiving' | 'winter-break' | 'spring-break' | 'summer-break') => {
    const today = new Date();

    if (preset === 'today') {
      const newRange = [{
        startDate: today,
        endDate: today,
        key: 'selection'
      }];

      setDateRange(newRange);
      onDateRangeChange(null, null);
      setShowDatePicker(false);
      setDatesCleared(true);
      return;
    }

    const { startDate, endDate } = getHolidayDates(preset);
    const newRange = [{
      startDate,
      endDate,
      key: 'selection'
    }];

    setDateRange(newRange);
    onDateRangeChange(startDate, endDate);
    setShowDatePicker(false);
    setDatesCleared(false);
  };

  return (
    <div className="filter-button-container">
      <button 
        className="filter-button filter-button-date"
        onClick={() => setShowDatePicker(!showDatePicker)}
      >
        <Calendar className="icon icon--sm" />
        <span>{formatDateRange()}</span>
        <ChevronDown className="icon icon--sm filter-chevron" />
      </button>
      
      {showDatePicker && (
        <div className="date-picker-dropdown" ref={pickerRef}>
          <DateRange
            editableDateInputs={false}
            months={isWideScreen ? 2 : 1}
            onChange={handleDateChange}
            moveRangeOnFirstSelection={false}
            ranges={dateRange}
            direction={isWideScreen ? "horizontal" : "vertical"}
            showDateDisplay={false}
            showSelectionPreview={true}
            showMonthAndYearPickers={true}
            showMonthArrow={true}
            showYearArrow={true}
            rangeColors={['#ff8a8a']}
            color="#ff8a8a"
          />
          
          <div className="date-picker-presets">
            <button 
              className="preset-button"
              onClick={() => handlePresetClick('next-month')}
            >
              Next Month
            </button>
            <button 
              className="preset-button"
              onClick={() => handlePresetClick('spring-break')}
            >
              Spring Break
            </button>
            <button 
              className="preset-button"
              onClick={() => handlePresetClick('summer-break')}
            >
              Summer Break
            </button>
            <button 
              className="preset-link"
              onClick={() => handlePresetClick('today')}
            >
              Clear Dates
            </button>        
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
